import React, { useEffect, useState } from 'react'
import './KaramalePrivacy.css'
import Header3 from './components/Header3'
import Header2 from './components/Header2'
import Header5 from './components/Header5'
import Header from './components/Header'

const S = ({ children }) => <strong>{children}</strong>

const SECTIONS = [
  {
    id: 'introduction',
    title: 'Introduction',
    intro: (
      <p>
        Karamale is committed to protecting your personal information and ensuring transparency in
        how we collect, use, and store data. This policy explains what we collect and how we use it.
      </p>
    ),
  },
  {
    id: 'collect',
    title: 'Information we collect',
    items: [
      <>
        <S>Personal details:</S> Name, email, phone number, ID (if required).
      </>,
      <>
        <S>Account activity:</S> Purchases, attendance, refunds, scans.
      </>,
      <>
        <S>Device info:</S> IP, browser, device, session logs.
      </>,
      <>
        <S>Payment data:</S> Transaction info (via gateway), excluding full card numbers.
      </>,
      <>
        <S>Location data:</S> Based on IP, used for discovery and fraud prevention.
      </>,
    ],
  },
  {
    id: 'use',
    title: 'How we use your information',
    items: [
      <>Deliver tickets to your Karamale account and enable entry via QR code.</>,
      <>Process secure payments and refunds.</>,
      <>Send notifications about events, changes, or cancellations.</>,
      <>Prevent fraud or duplicate refund misuse.</>,
      <>Improve platform experience and performance.</>,
    ],
  },
  {
    id: 'security',
    title: 'Data security',
    items: [
      <>Encrypted QR ticket validation and secure authentication.</>,
      <>Audit logs for scans, refunds, and account activity.</>,
      <>Role-based admin access for event organisers.</>,
    ],
  },
  {
    id: 'sharing',
    title: 'Data sharing',
    intro: (
      <p>
        We do <S>not sell</S> your personal data. We may share limited data with:
      </p>
    ),
    items: [
      <>Event organisers (ticket type, scan status).</>,
      <>Payment processors (e.g. PayFast) for transaction handling.</>,
      <>Law enforcement or regulators if legally required.</>,
    ],
  },
  {
    id: 'rights',
    title: 'Your rights',
    items: [
      <>Access, correct, or delete your data.</>,
      <>Opt out of marketing communications.</>,
      <>Request a record of your ticketing history.</>,
    ],
    outro: <p>Use your account dashboard or contact support to make a request.</p>,
  },
  {
    id: 'retention',
    title: 'Data retention',
    intro: (
      <p>
        Ticketing and transaction data is retained for up to <S>5 years</S>. QR scans and refund
        history may be stored longer for fraud prevention.
      </p>
    ),
  },
  {
    id: 'popia',
    title: 'POPIA compliance',
    intro: (
      <p>
        Karamale complies with the Protection of Personal Information Act (POPIA). We handle your
        data lawfully, transparently, and for legitimate purposes only.
      </p>
    ),
  },
  {
    id: 'contact',
    title: 'Contact us',
    items: [
      <>
        Email: <a href="mailto:support@karamale.com">support@karamale.com</a>
      </>,
      <>Dashboard: Use the "Privacy Request" option under Account Settings</>,
    ],
  },
]

function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0])
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length) {
          visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
          setActive(visible[0].target.id)
        }
      },
      { rootMargin: '-15% 0px -70% 0px' },
    )
    ids.forEach((id) => {
      const el = document.getElementById(id)
      if (el) obs.observe(el)
    })
    return () => obs.disconnect()
  }, [ids])
  return active
}

function KaramalePrivacy() {
  useEffect(() => {
    document.title = 'Karamale Privacy Policy'
  }, [])

  const active = useActiveSection(SECTIONS.map((s) => s.id))

  return (
    <>
      {localStorage.getItem('customername') !== null ? (
        <Header2 />
      ) : localStorage.getItem('businessname') !== null ? (
        <Header3 />
      ) : localStorage.getItem('partner') !== null ? (
        <Header5 />
      ) : (
        <Header />
      )}

      <main className="pv">
        <div className="pv-wrap">
          <header className="pv-hero">
            <h1>Karamale Privacy Policy</h1>
            <p>What we collect, why we collect it, and how you can control it.</p>
          </header>

          <div className="pv-body">
            <nav className="pv-nav" aria-label="Privacy policy sections">
              <ul>
                {SECTIONS.map(({ id, title }) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      className={active === id ? 'is-active' : ''}
                      aria-current={active === id ? 'true' : undefined}
                    >
                      {title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <article className="pv-content">
              {SECTIONS.map(({ id, title, intro, items, outro }, i) => (
                <section key={id} id={id} className="pv-section">
                  <h2>
                    <span className="pv-num">{i + 1}.</span>
                    {title}
                  </h2>
                  {intro}
                  {items && (
                    <ul>
                      {items.map((item, j) => (
                        <li key={j}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {outro && <div className="pv-outro">{outro}</div>}
                </section>
              ))}
            </article>
          </div>
        </div>
      </main>
    </>
  )
}

export default KaramalePrivacy
